import { type KeyboardEvent, useRef } from "react";
import { TextArea } from "@/shared/components/shadcn/TextArea";
import { Button } from "@/shared/components/shadcn/Button";
import { FormField } from "@/shared/components/ui/FormField";

interface MessageInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  disabled?: boolean;
  pending?: boolean;
  retrying?: boolean;
  readOnly?: boolean;
  error?: string;
  unavailableReason?: string;
}

export function MessageInput({
  value,
  onChange,
  onSend,
  disabled = false,
  pending = false,
  retrying = false,
  readOnly = false,
  error,
  unavailableReason,
}: MessageInputProps) {
  const composing = useRef(false);
  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey || composing.current
      || event.nativeEvent.isComposing || event.keyCode === 229) return;
    event.preventDefault();
    if (!readOnly && !disabled && !pending && value.trim()) event.currentTarget.form?.requestSubmit();
  };

  return (
    <form
      className="w-full min-w-0 space-y-2"
      aria-label="Reply"
      aria-busy={pending}
      onSubmit={(event) => {
        event.preventDefault();
        if (!disabled && !pending && value.trim()) onSend();
      }}
    >
      <div className="messages-composer">
        <FormField id="message-body" label="Message" className="messages-composer-field">
          <TextArea
            value={value}
            readOnly={readOnly || pending}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={handleKeyDown}
            onCompositionStart={() => { composing.current = true; }}
            onCompositionEnd={() => { composing.current = false; }}
            placeholder="Write a message..."
            rows={1}
            maxLength={1600}
            aria-describedby={error ? "message-send-error" : unavailableReason ? "message-unavailable" : undefined}
          />
        </FormField>
        <Button type="submit" disabled={disabled || pending || !value.trim()}>
          {pending ? "Sending..." : retrying ? "Check / retry original message" : "Send"}
        </Button>
      </div>
      {unavailableReason && <p id="message-unavailable" role="status" className="text-xs text-muted-foreground">{unavailableReason}</p>}
      {error && <p id="message-send-error" role="alert" className="text-sm text-destructive">{error}</p>}
    </form>
  );
}
